import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { UseFormRegisterReturn } from 'react-hook-form';

import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';

import type { LinkCopy } from './linkCopy';
import { linkUrlSchema } from './linkSchema';
import type { LinkView } from './linkState';

interface LinkUrlCardProps {
  view: LinkView;
  copy: LinkCopy;
  /** False for a read-only viewer, and after a write was refused with 403. */
  writable: boolean;
  /** The page form's address field. */
  field: UseFormRegisterReturn<'url'>;
  /** What the field holds now, typed or saved. */
  value: string;
  /** Why the address is refused, by the form's rules or the server's. */
  error?: string;
  /** Asks to remove the draft; given only when Remove link is offered (P4). */
  onRemove?: () => void;
  /** While a write is pending, or after a 403 (P18, P17). */
  removeDisabled?: boolean;
}

/**
 * The card holding the link's address: its header, the sentence that the
 * button appears once the link is published (P21, P33), the field and Test
 * Link.
 *
 * The field is the page form's, holding the saved draft until it is edited.
 * It is disabled when the link cannot be changed (P20, P17); the value stays
 * shown. Test Link is offered only while the field holds a valid secure
 * address, read-only viewers included (P15, P20). Remove link sits beside it,
 * only on a draft with an address (P4).
 */
export function LinkUrlCard({
  view,
  copy,
  writable,
  field,
  value,
  error,
  onRemove,
  removeDisabled = false,
}: LinkUrlCardProps) {
  const testable = linkUrlSchema.safeParse(value).success;

  return (
    <Paper
      variant="outlined"
      component="section"
      sx={{ p: { xs: 2, sm: 3 } }}
      data-testid={`arc-${view.kind}-url-card`}
    >
      <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
        {copy.cardTitle}
      </Typography>
      <Typography
        color="text.secondary"
        sx={{ mb: 2 }}
        data-testid={`arc-${view.kind}-publish-hint`}
      >
        {copy.cardDescription}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Field
          label={copy.fieldLabel}
          hint={copy.fieldHelper}
          error={error}
          testId={`arc-${view.kind}-url`}
        >
          <TextField
            {...field}
            disabled={!writable}
            slotProps={{ htmlInput: { inputMode: 'url' } }}
          />
        </Field>
        <Button
          variant="secondary"
          disabled={!testable}
          // What was typed, in a new tab that cannot reach back to the portal
          // and is not told where it came from. Nothing is recorded (P15, I6).
          onClick={() => window.open(value.trim(), '_blank', 'noopener,noreferrer')}
          aria-label="Test Link, opens in a new tab"
          data-testid={`arc-${view.kind}-test-link-button`}
          sx={{ flexShrink: 0, mt: { sm: 1 } }}
        >
          Test Link
        </Button>
        {onRemove ? (
          <Button
            variant="secondary"
            disabled={removeDisabled}
            onClick={onRemove}
            data-testid={`arc-${view.kind}-remove-button`}
            sx={{ flexShrink: 0, mt: { sm: 1 } }}
          >
            Remove link
          </Button>
        ) : null}
      </Box>
    </Paper>
  );
}
